/**
 * Native Logger Bridge
 * Ponte entre JS e native logging (OSLog/Logcat)
 */

import { Capacitor } from '@capacitor/core';

export type NativeLogLevel = 'debug' | 'info' | 'warn' | 'error' | 'fatal';

/**
 * Envia log para native platform (iOS OSLog / Android Logcat)
 */
export async function logToNative(
  level: NativeLogLevel,
  message: string,
  context: Record<string, any>
) {
  if (!Capacitor.isNativePlatform()) {
    return; // Apenas em plataformas nativas
  }

  try {
    // Tentar usar plugin customizado OSLog (se disponível)
    // Nota: Plugin customizado precisa ser registrado separadamente
    // Por ora, usar apenas console nativo que será capturado pelo Sentry

    // Fallback: usar console nativo (será capturado pelo Sentry)
    const logMessage = `[${level.toUpperCase()}] ${message}`;
    
    switch (level) {
      case 'debug':
        console.debug(logMessage, context);
        break;
      case 'info':
        console.info(logMessage, context);
        break;
      case 'warn':
        console.warn(logMessage, context);
        break;
      case 'error':
      case 'fatal':
        console.error(logMessage, context);
        
        // Enviar para Sentry se disponível
        if ((window as any).Sentry) {
          const Sentry = (window as any).Sentry;
          Sentry.captureException(new Error(message), {
            level: level === 'fatal' ? 'fatal' : 'error',
            extra: context,
          });
        }
        break;
    }
  } catch (error) {
    // Silent fail - não queremos quebrar o app por falha de logging
    console.error('Failed to log to native:', error);
  }
}

/**
 * Helper para verificar se native logging está disponível
 */
export function isNativeLoggingAvailable(): boolean {
  return Capacitor.isNativePlatform() && 
         (Capacitor.isPluginAvailable('OSLog') || true); // Console sempre disponível
}
